import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Register.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const decodeRef = (str) => {
  try {
    const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    return null;
  }
};

const cleanPhone = (raw) => {
  let p = (raw || '').replace(/[\s\-\.]/g, '');
  if (p.startsWith('+91')) p = p.slice(3);
  else if (/^91\d{10}$/.test(p)) p = p.slice(2);
  return p;
};

const Register = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
  });
  const [searchParams]                      = useSearchParams();
  const [referredById, setReferredById]     = useState(null);
  const [referredByName, setReferredByName] = useState('');
  const [phoneLocked, setPhoneLocked]       = useState(false);
  const [error, setError]                   = useState('');
  const [phoneError, setPhoneError]         = useState('');
  const [emailError, setEmailError]         = useState('');
  const [loading, setLoading]               = useState(false);
  const [success, setSuccess]               = useState(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!ref) return;
    const payload = decodeRef(ref);
    if (!payload) return;
    setForm(prev => ({
      ...prev,
      name:    payload.n || prev.name,
      email:   payload.e || prev.email,
      phone:   payload.p || prev.phone,
      address: payload.a || prev.address,
    }));
    if (payload.rid) setReferredById(payload.rid);
    if (payload.rn)  setReferredByName(payload.rn);
    if (payload.p)   setPhoneLocked(true);
  }, [searchParams]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    if (e.target.name === 'phone') setPhoneError('');
    if (e.target.name === 'email') setEmailError('');
  };

  const handlePhoneBlur = async () => {
    const cleaned = cleanPhone(form.phone);
    if (cleaned !== form.phone) {
      setForm(prev => ({ ...prev, phone: cleaned }));
    }
    if (!cleaned || !/^\d{10}$/.test(cleaned)) return;
    try {
      const res = await axios.get(`${API_URL}/authRoutes/check-phone`, {
        params: { phone: cleaned },
      });
      if (res.data.exists) {
        setPhoneError('This phone number is already registered. Please sign in instead.');
      }
    } catch {
      // silently ignore — server-side will also catch duplicates
    }
  };

  const handleEmailBlur = async () => {
    const trimmed = form.email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    try {
      const res = await axios.get(`${API_URL}/authRoutes/check-email`, {
        params: { email: trimmed },
      });
      if (res.data.exists) {
        setEmailError('This email is already registered. Please sign in instead.');
      }
    } catch {
      // silently ignore
    }
  };

  const validate = () => {
    if (!form.name.trim()) return 'Full name is required.';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return 'Enter a valid email address.';
    if (emailError) return emailError;
    const phone = cleanPhone(form.phone);
    if (!phone) return 'Phone number is required.';
    if (!/^\d{10}$/.test(phone)) return 'Enter a valid 10-digit phone number.';
    if (phoneError) return phoneError;
    if (!form.password) return 'Password is required.';
    if (form.password.length < 4) return 'Password must be at least 4 characters.';
    if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    if (!form.address.trim()) return 'Address is required.';
    if (!/\b\d{6}\b/.test(form.address)) return 'Please include your 6-digit pincode in the address.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const cleanedPhone = cleanPhone(form.phone);
    setLoading(true);
    setError('');

    try {
      const pincodes = [...new Set(form.address.match(/\b\d{6}\b/g) || [])];
      let deliverable = false;
      for (const pincode of pincodes) {
        const pincodeRes = await axios.get(`${API_URL}/authRoutes/check-pincode`, {
          params: { pincode },
        });
        if (pincodeRes.data.deliverable) { deliverable = true; break; }
      }
      if (!deliverable) {
        setError('Currently we are not delivering at this pincode.');
        setLoading(false);
        return;
      }

      await axios.post(`${API_URL}/authRoutes/register-customer`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase() || undefined,
        phone: cleanedPhone,
        password: form.password,
        address: form.address.trim(),
        ...(referredById && { referred_by_id: referredById }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const cleanedPhone = cleanPhone(form.phone);
    return (
      <div className="register-container">
        <div className="register-background">
          <div className="background-shape shape-1"></div>
          <div className="background-shape shape-2"></div>
          <div className="background-shape shape-3"></div>
        </div>
        <div className="register-card">
          <div className="register-success-icon">&#10003;</div>
          <h2 className="register-success-title">You're registered!</h2>
          <p className="register-success-msg">
            Your account is ready. Sign in using your phone number{' '}
            <strong>{cleanedPhone}</strong> as your username.
          </p>
          <button className="register-btn" onClick={() => navigate('/login')}>
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="register-background">
        <div className="background-shape shape-1"></div>
        <div className="background-shape shape-2"></div>
        <div className="background-shape shape-3"></div>
      </div>

      <div className="register-card">
        <div className="register-header">
          <img src="/logo.webp" alt="The New Luxury" className="register-logo" />
        </div>

        <div className="register-welcome">
          <h2>Create Account</h2>
          <p>Join us and start shopping</p>
        </div>

        {error && (
          <div className="register-error">
            <span>&#9888;</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="register-form" noValidate>

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <div className="input-wrapper">
              <span className="input-icon">&#128100;</span>
              <input
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                disabled={loading}
                autoComplete="name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <div className="input-wrapper">
              <span className="input-icon">&#128222;</span>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={form.phone}
                onChange={phoneLocked ? undefined : handleChange}
                onBlur={phoneLocked ? undefined : handlePhoneBlur}
                placeholder="10-digit mobile number"
                disabled={loading}
                readOnly={phoneLocked}
                style={phoneLocked ? { background: '#f0f0f0', color: '#666', cursor: 'not-allowed' } : {}}
                autoComplete="tel"
              />
            </div>
            {phoneError && (
              <p className="register-field-error">{phoneError}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email <span className="register-optional">(optional)</span></label>
            <div className="input-wrapper">
              <span className="input-icon">&#9993;</span>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                placeholder="Enter your email (optional)"
                disabled={loading}
                autoComplete="email"
              />
            </div>
            {emailError && (
              <p className="register-field-error">{emailError}</p>
            )}
          </div>

          <div className="register-row">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <span className="input-icon">&#128274;</span>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Min. 4 characters"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <span className="input-icon">&#128274;</span>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Delivery Address</label>
            <textarea
              id="address"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Enter your full delivery address"
              rows={3}
              disabled={loading}
              className="register-textarea"
            />
          </div>

          {referredByName && (
            <div className="form-group">
              <label htmlFor="referredBy">Referred By</label>
              <div className="input-wrapper">
                <span className="input-icon">&#128101;</span>
                <input
                  type="text"
                  id="referredBy"
                  value={referredByName}
                  readOnly
                  style={{ background: '#f0f0f0', color: '#999', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          )}

          <button type="submit" className="register-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                <span>Creating account…</span>
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="register-signin">
          Already have an account?{' '}
          <button className="register-signin-link" onClick={() => navigate('/login')}>
            Sign In
          </button>
        </div>

        <div className="register-footer">
          <p>&#169; {new Date().getFullYear()} The New Luxury. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Register;
